import { useId, useState, type ReactNode } from 'react'

import type { NotifyOwnerRequest, NotifyOwnerResponse } from '@cattle/contracts'

import { Button, Field, Modal, Select, TextInput } from '../../components'
import { queryKeys } from '../../fixtures'
import { post } from '../../lib/apiClient'
import { t } from '../../lib/i18n'
import { useApiMutation } from '../../lib/mutations'
import { MESSAGE_CHANNELS, channelLabel, type MessageChannel } from './messageChannels'

/**
 * "Notify owner" — P-08's row action, addressed to ONE book.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT THE SUGGEST COMPOSER
 * ---------------------------------------------------------------------------
 * `suggestUpgrade.tsx` looks almost identical and is deliberately not reused:
 * it posts `POST /platform/broadcast`, whose audience is a FILTER, so the row
 * that was clicked there is a source of words and never an addressee — which is
 * exactly what its `notARecipient` banner has to say before the button is
 * pressed.
 *
 * This composer posts `POST /platform/businesses/:id/notify` (P14), which names
 * the tenant in the path. The row here really is the recipient, so there is no
 * warning to write — the subtitle simply says who it goes to. The two flows
 * share the channel list and the receipt's wording, and nothing else.
 *
 * ---------------------------------------------------------------------------
 * `reason` IS NOT DECORATION
 * ---------------------------------------------------------------------------
 * `notifyOwnerRequest.reason` is required, and it is written to the access log
 * alongside the message — the trader can read it in his own history. So it is
 * labelled as something he will read, not as an internal justification field.
 *
 * ---------------------------------------------------------------------------
 * G22 — WHAT THE RECEIPT MAY AND MAY NOT CLAIM
 * ---------------------------------------------------------------------------
 * `notifyOwnerResponse.delivered` is a stated fact, not an implied one. While
 * no provider is configured it comes back `false` with an `undeliveredReason`,
 * and the receipt below says plainly that nothing was sent. That is READ from
 * the response rather than hard-coded, so the day a provider is wired up this
 * same screen tells the truth about it without being edited — a receipt that
 * asserts non-delivery in its own words is a receipt that will one day lie.
 */

export interface NotifyOwnerTarget {
  /** The tenant this goes to. It is the `:id` in the path, not a filter. */
  businessId: string
  /** Its name, so the composer can say who is being written to. */
  business: string
  /** The row's problem, which seeds the words the reader then edits. */
  what: string
}

export interface NotifyOwnerComposer {
  /** Opens the composer, seeded from the row the reader clicked. */
  open: (target: NotifyOwnerTarget) => void
  /** Render once, anywhere in the screen. Both modals live in here. */
  element: ReactNode
}

export function useNotifyOwner(): NotifyOwnerComposer {
  const [target, setTarget] = useState<NotifyOwnerTarget | null>(null)
  const [channel, setChannel] = useState<MessageChannel>('WHATSAPP')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [reason, setReason] = useState('')
  const [receipt, setReceipt] = useState<NotifyOwnerResponse | null>(null)

  const subjectId = useId()
  const channelId = useId()
  const bodyId = useId()
  const reasonId = useId()

  const send = useApiMutation<NotifyOwnerTarget, NotifyOwnerResponse>({
    run: async (to) => {
      const request: NotifyOwnerRequest = {
        channel,
        subject: subject.trim(),
        body: body.trim(),
        reason: reason.trim(),
      }
      return post<NotifyOwnerResponse>(
        `/platform/businesses/${encodeURIComponent(to.businessId)}/notify`,
        request,
      )
    },
    /* Nothing is delivered today, but the attempt is logged either way — the
       access log is the one screen that moves, so it is the one refetched. */
    invalidates: [queryKeys.accessLog],
    onDone: (result) => {
      setTarget(null)
      setReceipt(result)
    },
  })

  /* Seeded, not sent. The human edits the words and presses the button
     himself; there is no path here that posts on a single click. */
  const open = (next: NotifyOwnerTarget) => {
    setSubject(t('p.notify.subjectSeed', { what: next.what }))
    setBody(t('p.notify.bodySeed', { what: next.what }))
    // Deliberately NOT seeded. The message can be seeded because the reader
    // edits it; the reason is his own statement of why he reached for this
    // control, and the trader reads it back in his history.
    setReason('')
    setChannel('WHATSAPP')
    setTarget(next)
  }

  /* The contract's own minimums — 3 characters on each of the three strings.
     Checked here so the button is dead rather than the server refusing. */
  const ready =
    subject.trim().length >= 3 && body.trim().length >= 3 && reason.trim().length >= 3

  const element = (
    <>
      <Modal
        open={target !== null}
        onClose={() => setTarget(null)}
        title={t('act.notifyOwner')}
        subtitle={t('p.notify.addressed', { business: target?.business ?? '' })}
        footer={
          <>
            <Button variant="ghost" onClick={() => setTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!ready || send.isPending}
              onClick={() => {
                if (target) send.mutate(target)
              }}
            >
              {t('p.notify.send')}
            </Button>
          </>
        }
      >
        <div className="grid gap-x-3.5 md:grid-cols-2">
          <Field required label={t('p.settings.broadcastSubject')} htmlFor={subjectId}>
            <TextInput id={subjectId} value={subject} onChange={setSubject} className="max-w-none" />
          </Field>
          <Field label={t('p.settings.broadcastChannel')} htmlFor={channelId}>
            <Select<MessageChannel>
              id={channelId}
              value={channel}
              onChange={setChannel}
              options={MESSAGE_CHANNELS.map((option) => ({
                value: option,
                label: channelLabel(option),
              }))}
            />
          </Field>
        </div>

        <Field label={t('p.settings.broadcastLabel')} htmlFor={bodyId}>
          <textarea
            id={bodyId}
            rows={5}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className="w-full rounded-[10px] border border-glass-border bg-glass px-3.5 py-3 text-[13.5px] leading-relaxed font-semibold text-dark outline-none placeholder:font-medium placeholder:text-light"
          />
        </Field>

        {/* The trader reads this one, and the hint says so beside the field —
            not on the receipt, where a reader learns it too late to choose
            different words. */}
        <Field
          required
          label={t('p.notify.reasonLabel')}
          hint={t('p.notify.reasonHint')}
          htmlFor={reasonId}
        >
          <TextInput id={reasonId} value={reason} onChange={setReason} className="max-w-none" />
        </Field>

        {/* Said before the button is pressed, not only on the receipt — and in
            P-11's words, because two screens describing the same undelivered
            message differently is how one of them ends up describing it wrongly. */}
        <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('p.settings.broadcastNote')}
        </p>
      </Modal>

      <Modal
        open={receipt !== null}
        onClose={() => setReceipt(null)}
        title={
          receipt?.delivered
            ? t('p.notify.sentTitle')
            : t('p.settings.broadcastReceiptTitle')
        }
        footer={
          <Button variant="primary" onClick={() => setReceipt(null)}>
            {t('act.close')}
          </Button>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-sub">
          {t('p.notify.receipt', {
            name: receipt?.ownerName ?? '',
            id: receipt?.notificationId ?? '',
          })}
        </p>

        {receipt?.delivered ? (
          <p className="mt-3 rounded-r border border-success/30 bg-success/10 p-3 text-[12.5px] leading-relaxed text-success">
            {t('p.notify.wasSent', { channel: channelLabel(channel) })}
          </p>
        ) : (
          /* The server says why. It is preferred over anything written here,
             because it is the half of this that changes when G22 is closed. */
          <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
            {receipt?.undeliveredReason ?? t('p.settings.broadcastNote')}
          </p>
        )}
      </Modal>
    </>
  )

  return { open, element }
}
